import { useEffect, useState } from 'react';
import { Download, X, Share, Plus, CheckCircle2, Smartphone } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * InstallPrompt — post-login nudge to add the app to the home screen.
 *
 * Three paths, detected automatically:
 *
 * 1. Already installed (running standalone) → render nothing.
 *
 * 2. Chrome / Edge / Android / desktop-with-beforeinstallprompt:
 *    We capture the browser's `beforeinstallprompt` event and offer a
 *    one-tap "Install" button that calls `.prompt()` on it. The OS
 *    dialog takes over from there.
 *
 * 3. iOS Safari (no beforeinstallprompt API):
 *    We detect iOS and show a short, graphic instruction card:
 *    "Tap Share → Add to Home Screen". There is no programmatic
 *    install on iOS — user must do it — but a clear UI with an
 *    arrow and the Share glyph makes it a 5-second task.
 *
 * Shown once. Dismissal persists in localStorage for 14 days so we're
 * not nagging a provider who declined during orientation. Re-shows
 * after 14 days unless the app is detected as installed in the meantime.
 */

const DISMISS_KEY = 'emspcr-install-dismissed-at';
const DISMISS_DAYS = 14;

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  // iPad-on-iPadOS-13+ reports as Mac with touch support — include that.
  return /iPad|iPhone|iPod/.test(ua)
    || (ua.includes('Mac') && 'ontouchend' in document);
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  // Safari-specific
  const nav = navigator as unknown as { standalone?: boolean };
  return nav.standalone === true;
}

function wasRecentlyDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    const days = (Date.now() - ts) / 86_400_000;
    return days < DISMISS_DAYS;
  } catch {
    return false;
  }
}

export function InstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [deferredEvt, setDeferredEvt] = useState<BeforeInstallPromptEvent | null>(null);
  const [ios, setIos] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // Already installed — never show.
    if (wasRecentlyDismissed()) return;

    setIos(isIOS());

    // beforeinstallprompt is the Chromium path.
    const onBefore = (e: Event) => {
      e.preventDefault(); // keep it so we can fire it ourselves
      setDeferredEvt(e as BeforeInstallPromptEvent);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBefore);

    // If we already got the event earlier (e.g. app was backgrounded),
    // Chrome replays it on the next focus.
    const onInstalled = () => { setInstalled(true); setVisible(false); };
    window.addEventListener('appinstalled', onInstalled);

    // iOS doesn't fire beforeinstallprompt — show the manual instructions
    // after a short delay so the prompt doesn't interrupt the first
    // post-login render.
    if (isIOS()) {
      const t = window.setTimeout(() => setVisible(true), 1200);
      return () => {
        window.clearTimeout(t);
        window.removeEventListener('beforeinstallprompt', onBefore);
        window.removeEventListener('appinstalled', onInstalled);
      };
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBefore);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  function dismiss() {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
  }

  async function doInstall() {
    if (!deferredEvt) return;
    await deferredEvt.prompt();
    const { outcome } = await deferredEvt.userChoice;
    if (outcome === 'accepted') {
      setInstalled(true);
      setVisible(false);
    } else {
      dismiss();
    }
  }

  if (installed) {
    return (
      <div className="fixed bottom-4 right-4 z-40 border border-emerald-700 bg-emerald-950/90 text-emerald-200 px-3 py-2 text-xs font-mono uppercase tracking-wider flex items-center gap-2 shadow-lg">
        <CheckCircle2 size={14} /> App installed
      </div>
    );
  }

  if (!visible) return null;

  return (
    <div className="fixed inset-x-3 bottom-3 z-40 flex justify-center pointer-events-none">
      <div className="pointer-events-auto w-full max-w-md border border-slate-700 bg-slate-900 shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-800 bg-slate-950 px-3 py-1.5">
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-blue-300">
            <Smartphone size={12} />
            Install EMSPCR
          </div>
          <button
            onClick={dismiss}
            className="text-slate-500 hover:text-slate-200"
            aria-label="Dismiss install prompt"
          >
            <X size={14} />
          </button>
        </div>

        <div className="p-3 space-y-2">
          <p className="text-sm text-slate-200 leading-snug">
            Add EMSPCR to your home screen for faster launch and full
            offline operation on the apparatus.
          </p>

          {ios ? (
            <IOSInstructions />
          ) : deferredEvt ? (
            <div className="flex gap-2">
              <button onClick={doInstall} className="btn-primary gap-1.5 text-sm flex-1">
                <Download size={14} />
                Install
              </button>
              <button onClick={dismiss} className="btn-secondary text-xs">
                Not now
              </button>
            </div>
          ) : (
            <div className="text-xs text-slate-400">
              Your browser will prompt when it's ready. Look for the install
              icon near the address bar.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function IOSInstructions() {
  return (
    <div className="text-xs text-slate-300 space-y-1.5">
      <div className="flex items-center gap-2">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center bg-slate-800 border border-slate-600 font-mono text-[10px] text-slate-300">1</span>
        <span>Tap the <span className="inline-flex items-center gap-1 px-1 py-0.5 bg-slate-800 border border-slate-700 font-mono"><Share size={11} />Share</span> button at the bottom of Safari.</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center bg-slate-800 border border-slate-600 font-mono text-[10px] text-slate-300">2</span>
        <span>Scroll and tap <span className="inline-flex items-center gap-1 px-1 py-0.5 bg-slate-800 border border-slate-700 font-mono"><Plus size={11} />Add to Home Screen</span>.</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center bg-slate-800 border border-slate-600 font-mono text-[10px] text-slate-300">3</span>
        <span>Confirm. The red cross icon appears on your home screen.</span>
      </div>
    </div>
  );
}
